import { useState } from 'react';
import { Link } from 'react-router';
import { MdMarkEmailRead } from 'react-icons/md';
import { FormInput } from '@podus/ui';
import AuthLayout from '@/components/AuthLayout';
import { api, ApiError } from '@/lib/api';

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.trim() || busy) return;

        setError('');
        setBusy(true);
        try {
            await api.post('/ambassador/forgot-password', { email: email.trim().toLowerCase() });
            setSent(true);
        } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Could not send the reset link. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    if (sent) {
        return (
            <AuthLayout
                title="Check your inbox"
                subtitle="If that email belongs to an ambassador account, a reset link is on its way."
                footer={
                    <Link to="/sign-in" className="font-bold text-primary">
                        Back to sign in
                    </Link>
                }
            >
                <div className="rounded-xl border border-[#E0E0E0] bg-[#fafafa] p-6">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <MdMarkEmailRead size={22} />
                    </span>
                    <p className="mt-4 text-[14px] leading-6 text-[#747474]">
                        The link is single-use and expires in 30 minutes. If it doesn&rsquo;t arrive within a few
                        minutes, check your spam folder.
                    </p>
                </div>
            </AuthLayout>
        );
    }

    return (
        <AuthLayout
            title="Reset your password"
            subtitle="We'll email you a link to choose a new one."
            footer={
                <Link to="/sign-in" className="font-bold text-primary">
                    Back to sign in
                </Link>
            }
        >
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {error && (
                    <div
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
                    >
                        {error}
                    </div>
                )}

                <FormInput
                    id="forgot-email"
                    name="email"
                    type="email"
                    title="Email"
                    placeholder="you@example.com"
                    autoComplete="email"
                    required
                    variant="white"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                />

                <button
                    type="submit"
                    disabled={!email.trim() || busy}
                    className="mt-2 h-12 w-full rounded-[10px] bg-secondary text-[14px] font-bold text-white transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                    {busy ? 'Sending…' : 'Send reset link'}
                </button>
            </form>
        </AuthLayout>
    );
}
